// homeActivityFilters.js

const AUTHORING_CHANGE_KINDS = new Set([
  'item_added',
  'item_removed',
  'item_text_changed',
  'items_reordered',
  'checklist_created',
  'checklist_deleted',
  'checklist_renamed',
  'assignees_changed',
]);

const PROGRESS_CHANGE_KINDS = new Set([
  'item_completed',
  'item_reopened',
]);

function listChangeKinds(event) {
  return (Array.isArray(event?.changes) ? event.changes : [])
    .map((change) => String(change?.changeKind || '').trim())
    .filter(Boolean);
}

function isChecklistSystemEvent(event) {
  const kind = String(event?.kind || '').trim();
  const type = String(event?.type || '').trim();
  return kind === 'checklist-changed' || type === 'node_checklist_changed';
}

/**
 * Lifted workflow check-off that Home treats as a detail.
 *
 * @param {object|null|undefined} event
 * @returns {boolean}
 */
export function isHomeWorkflowProgressDetail(event) {
  if (!event) return false;
  if (String(event.prominence || '').trim() === 'detail') return true;
  return String(event.source || '').trim() === 'workflow_task_checklist';
}

/**
 * TASK-local check-off (not the host lift). Hidden on Home to avoid duplicates.
 *
 * @param {object|null|undefined} event
 * @returns {boolean}
 */
export function isHomeTaskLocalCheckOff(event) {
  if (!isChecklistSystemEvent(event)) return false;
  if (isHomeWorkflowProgressDetail(event)) return false;
  return listChangeKinds(event).some((kind) => PROGRESS_CHANGE_KINDS.has(kind));
}

/**
 * Item authoring noise — never on Home.
 *
 * @param {object|null|undefined} event
 * @returns {boolean}
 */
export function isHomeAuthoringNoise(event) {
  if (!isChecklistSystemEvent(event)) return false;
  const kinds = listChangeKinds(event);
  if (kinds.length === 0) return false;
  return kinds.every((kind) => AUTHORING_CHANGE_KINDS.has(kind));
}

/**
 * Create events for playbook-minted step TASKs (provisioning noise).
 *
 * @param {object|null|undefined} item
 * @returns {boolean}
 */
export function isHomeWorkflowStepMintCreate(item) {
  if (item?.node?.workflowStepMint !== true) return false;
  const event = item?.event;
  if (!event) return false;
  const kind = String(event?.kind || '').trim();
  const type = String(event?.type || '').trim();
  return kind === 'created' || type === 'node_created';
}

/**
 * Denormalized "updated checklist" / n/m field summary.
 *
 * @param {object|null|undefined} event
 * @returns {boolean}
 */
export function isHomeChecklistFieldSummary(event) {
  const kind = String(event?.kind || '').trim();
  const type = String(event?.type || '').trim();
  if (kind !== 'field-changed' && type !== 'node_field_changed') return false;

  const paths = [
    event?.field,
    event?.fieldPath,
    ...(Array.isArray(event?.changes) ? event.changes : []).flatMap((change) => ([
      change?.fieldPath,
      change?.field,
      change?.fieldLabel,
    ])),
  ].map((value) => String(value || '').trim().toLowerCase());

  return paths.some((path) => {
    if (!path) return false;
    const leaf = path.split('.').pop() || path;
    return leaf === 'checklist' || path.endsWith('.checklist') || path === 'checklist';
  });
}

/**
 * @param {object|null|undefined} item
 * @param {{ includeDetails?: boolean }} [options]
 * @returns {boolean}
 */
export function shouldShowHomeActivityItem(item, { includeDetails = false } = {}) {
  if (!item) return false;
  if (item.kind !== 'system') return true;

  const event = item.event;
  if (!event) return true;
  if (isHomeWorkflowStepMintCreate(item)) return false;
  if (isHomeAuthoringNoise(event)) return false;
  if (isHomeChecklistFieldSummary(event)) return false;
  if (isHomeTaskLocalCheckOff(event)) return false;
  if (isHomeWorkflowProgressDetail(event) && includeDetails !== true) return false;
  return true;
}
